import * as React from "react"
import type { BoardItem, BoardView, Kind } from "../lib/client"
import { crumbs, prettify } from "../lib/tree"
import { Icon } from "./Icon"
import { New } from "./New"

/**
 * A board, in one of two views over the same cards.
 *
 * **Columns** answer "what is moving" — every item by state, which is the question you ask
 * while working. **Catalog** answers "what are we building" — epics with their features
 * underneath, each feature's state on its own card. Both read the same items from the same
 * files; neither is a copy of the other, which is why switching is free and why they cannot
 * disagree.
 *
 * **The gate is not rendered here, it is enforced by the client.** This shows what the next
 * column will ask for and lets the move be attempted anyway; if the file is missing the
 * client refuses and the reason appears under the card. A surface that greyed the button
 * out using a rule it had reimplemented would eventually disagree with the writer that
 * actually decides — and the disagreement would look like a bug in the board rather than a
 * rule nobody could see.
 *
 * Moving **back** is never gated, so the left arrow is always live. Admitting something was
 * not ready has to be cheaper than parking it in the wrong column.
 */
export const Board: React.FC<{
  view: BoardView
  title: string
  summary: string
  kinds: Kind[]
  boards: Array<{ path: string; title: string; template: string }>
  parents: Array<{ path: string; title: string; kind: string; where?: string }>
  titleOf: (path: string) => string
  /** Which view, from the address bar — so a link carries it and back returns to it. */
  mode: string
  onMode: (mode: string) => void
  onOpen: (path: string) => void
  onEnter: (dir: string) => void
  onMove: (path: string, state: string) => Promise<void>
  onPromote: (path: string, board: string, kind: string) => Promise<void>
  onCreate: (kind: string, name: string, itemParent: string | null) => Promise<void>
}> = ({ view, title, summary, kinds, boards, parents, titleOf, mode, onMode, onOpen,
        onEnter, onMove, onPromote, onCreate }) => {
  const [err, setErr] = React.useState<Record<string, string>>({})
  const [busy, setBusy] = React.useState<string | null>(null)
  const states = view.board.states
  const dir = view.path.slice(0, view.path.lastIndexOf("/"))
  // A catalog is a hierarchy, so it needs a level to hang one from.
  const hasEpics = view.board.kinds.includes("epic")
  const catalog = hasEpics && mode === "catalog"

  const guard = (path: string, run: () => Promise<void>) => async () => {
    setBusy(path)
    setErr(e => ({ ...e, [path]: "" }))
    try {
      await run()
    } catch (e) {
      setErr(x => ({ ...x, [path]: e instanceof Error ? e.message : String(e) }))
    } finally {
      setBusy(null)
    }
  }

  const card = (item: BoardItem) => (
    <Card key={item.path} item={item} states={states} boards={boards} busy={busy === item.path}
          artifacts={view.artifacts} error={err[item.path]} titleOf={titleOf}
          onOpen={onOpen}
          onMove={s => guard(item.path, () => onMove(item.path, s))()}
          onPromote={(b, k) => guard(item.path, () => onPromote(item.path, b, k))()} />
  )

  return (
    <>
      <header className="sf-doc-head">
        <nav className="sf-crumbs" aria-label="Where you are">
          {crumbs(dir).slice(0, -1).map(c => (
            <button key={c} className="sf-crumb" onClick={() => onEnter(c)}>
              {prettify(c.split("/").pop() ?? c)}
            </button>
          ))}
        </nav>
        <p className="sf-kicker">board</p>
        <h1>{title}</h1>
        {summary && <p className="sf-lede">{summary}</p>}
        <div className="sf-meta">
          <span className="sf-chip"><b>{view.items.length}</b> items</span>
          {hasEpics && (
            <div className="sf-views" role="group" aria-label="View">
              <button className={`sf-view-opt ${catalog ? "" : "is-active"}`}
                      onClick={() => onMode("")}>Board</button>
              <button className={`sf-view-opt ${catalog ? "is-active" : ""}`}
                      onClick={() => onMode("catalog")}>Catalog</button>
            </div>
          )}
          <New kinds={kinds} onCreate={onCreate} label="+ Add" parents={parents} />
        </div>
      </header>

      {catalog
        ? <Catalog items={view.items} card={card} onOpen={onOpen} />
        : (
          <div className="sf-board">
            {states.map(state => {
              const items = view.items.filter(i => i.state === state)
              return (
                <section key={state} className="sf-col">
                  <h2 className="sf-col-head">
                    {prettify(state)} <span className="sf-count">{items.length || "—"}</span>
                  </h2>
                  {items.map(card)}
                </section>
              )
            })}
          </div>
        )}
    </>
  )
}

/**
 * Epics, with their features underneath.
 *
 * What is being built, rather than what is moving. Features with no epic get a group of
 * their own and it is **named**, not hidden — a feature answering to nothing above it is
 * ordinary here, and quietly leaving it off would make the catalog claim to be the whole
 * product when it is not.
 */
const Catalog: React.FC<{
  items: BoardItem[]
  card: (item: BoardItem) => React.ReactNode
  onOpen: (path: string) => void
}> = ({ items, card, onOpen }) => {
  const epics = items.filter(i => i.kind === "epic")
  const under = (epicPath: string) =>
    items.filter(i => i.kind !== "epic" && i.parent === epicPath)
  const orphans = items.filter(i => i.kind !== "epic"
    && !(i.parent && epics.some(e => e.path === i.parent)))

  return (
    <div className="sf-catalog">
      {epics.map(epic => (
        <section key={epic.path} className="sf-epic">
          <header className="sf-epic-head">
            <span className="sf-card-kind"><Icon kind="epic" />epic</span>
            <button className="sf-rowlink" onClick={() => onOpen(epic.path)}>{epic.title}</button>
            <span className={`sf-state is-${epic.state}`}>{prettify(epic.state)}</span>
          </header>
          {epic.summary && <p className="sf-epic-note">{epic.summary}</p>}
          <div className="sf-epic-items">
            {under(epic.path).length
              ? under(epic.path).map(card)
              : <p className="sf-quiet">No features under this yet.</p>}
          </div>
        </section>
      ))}

      {!!orphans.length && (
        <section className="sf-epic is-orphans">
          <header className="sf-epic-head">
            <span className="sf-card-kind">no epic</span>
            <span className="sf-quiet">answering to nothing above them</span>
          </header>
          <div className="sf-epic-items">{orphans.map(card)}</div>
        </section>
      )}

      {!epics.length && !orphans.length && (
        <p className="sf-note"><b>Nothing on this board yet.</b>Use <b>+ Add</b> to start.</p>
      )}
    </div>
  )
}

/** One card, identical in both views — two drawings of one item would drift apart. */
const Card: React.FC<{
  item: BoardItem
  states: string[]
  boards: Array<{ path: string; title: string; template: string }>
  busy: boolean
  artifacts: Record<string, string>
  error?: string
  titleOf: (path: string) => string
  onOpen: (path: string) => void
  onMove: (state: string) => void
  onPromote: (board: string, kind: string) => void
}> = ({ item, states, boards, busy, artifacts, error, titleOf, onOpen, onMove, onPromote }) => {
  const at = states.indexOf(item.state)
  return (
    <article className={`sf-item is-${item.kind}`}>
      <div className="sf-item-top">
        <span className="sf-card-kind"><Icon kind={item.kind} />{item.kind}</span>
        <button className="sf-rowlink" onClick={() => onOpen(item.path)}>{item.title}</button>
      </div>

      {item.parent && <p className="sf-item-parent">part of {titleOf(item.parent)}</p>}

      {!!item.artifacts.length && (
        <p className="sf-artifacts">
          {item.artifacts.map(a => <span key={a} className="sf-artifact has">{a}</span>)}
        </p>
      )}

      {!!item.needs.length && (
        <p className="sf-artifacts">
          {item.needs.map(a => (
            <span key={a} className="sf-artifact wants"
                  title={artifacts[a] ?? "required by the next column"}>
              {/* `parent:scope.md` is a requirement on the item above, not a filename
                  beside this one — say so rather than showing the prefix and letting
                  somebody go looking for the wrong file. */}
              {a.startsWith("parent:") ? `↑ ${a.slice("parent:".length)}` : a}
            </span>
          ))}
        </p>
      )}

      <div className="sf-item-actions">
        <span className={`sf-state is-${item.state}`}>{prettify(item.state)}</span>
        {item.kind === "idea" ? (
          /* An idea has two **exits**, not a column to its right: forward onto a board, or
             into the archive. Stepping it right would set `promoted` without promoting
             anything — a state that lies about work existing somewhere else. */
          item.state === "inbox" ? (
            <>
              {!!boards.length && <Promote boards={boards} busy={busy} onPromote={onPromote} />}
              <button className="sf-ghost" disabled={busy}
                      onClick={() => onMove("archived")}>archive</button>
            </>
          ) : item.state === "archived" ? (
            <button className="sf-ghost" disabled={busy}
                    onClick={() => onMove("inbox")}>back to inbox</button>
          ) : (
            /* Promoted: no way back. The card it became exists on another board, and
               un-promoting would not remove it — only let the idea be promoted twice. */
            <button className="sf-ghost" onClick={() => onOpen(item.meta.promoted_to)}>
              open what it became
            </button>
          )
        ) : (
          <>
            <button className="sf-ghost" disabled={at <= 0 || busy}
                    onClick={() => onMove(states[at - 1])}
                    title={at > 0 ? `back to ${states[at - 1]}` : ""}>←</button>
            <button className="sf-ghost" disabled={at < 0 || at >= states.length - 1 || busy}
                    onClick={() => onMove(states[at + 1])}
                    title={item.next ? `to ${item.next}` : ""}>→</button>
          </>
        )}
      </div>

      {error && <p className="sf-err">{error}</p>}
    </article>
  )
}

/**
 * Sending an idea forward.
 *
 * An inbox with no exit is a graveyard, so this is the exit: pick a board, pick what it
 * becomes there. The idea itself is kept and marked rather than deleted — the wording an
 * idea was had in is often the only part worth keeping.
 */
const Promote: React.FC<{
  boards: Array<{ path: string; title: string; template: string }>
  busy: boolean
  onPromote: (board: string, kind: string) => void
}> = ({ boards, busy, onPromote }) => {
  const [open, setOpen] = React.useState(false)
  const [board, setBoard] = React.useState(boards[0]?.path ?? "")
  const kinds = KINDS_BY_TEMPLATE[boards.find(b => b.path === board)?.template ?? ""] ?? []
  const [kind, setKind] = React.useState(kinds[0] ?? "")

  if (!open) {
    return <button className="sf-ghost" onClick={() => setOpen(true)} disabled={busy}>
      send forward
    </button>
  }
  return (
    <div className="sf-promote">
      <select value={board} onChange={e => {
        setBoard(e.target.value)
        const next = KINDS_BY_TEMPLATE[
          boards.find(b => b.path === e.target.value)?.template ?? ""] ?? []
        setKind(next[0] ?? "")
      }}>
        {boards.map(b => <option key={b.path} value={b.path}>{b.title}</option>)}
      </select>
      <select value={kind} onChange={e => setKind(e.target.value)}>
        {kinds.map(k => <option key={k} value={k}>{k}</option>)}
      </select>
      <button className="sf-btn" disabled={busy || !board || !kind}
              onClick={() => onPromote(board, kind)}>go</button>
      <button className="sf-ghost" onClick={() => setOpen(false)}>cancel</button>
    </div>
  )
}

/**
 * What each board template holds, for the one moment the surface has to guess before
 * asking: the promote dialog, which needs a kind before the board has been opened. The
 * client refuses anything wrong, so this is a shortlist and never an authority.
 */
const KINDS_BY_TEMPLATE: Record<string, string[]> = {
  product: ["epic", "feature"],
  tasks: ["task"],
  tech: ["story", "task", "bug"],
}
